import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const ContractManagement = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);

      // expert_id FK → expert_profiles; client_id FK → auth.users (not joinable via PostgREST)
      const { data: contractsData, error } = await supabase
        ?.from('marketplace_contracts')
        ?.select(`
          *,
          expert:expert_id(id, full_name, commission_pct)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Resolve client emails from user_profiles (same UUID as auth.users)
      const clientIds = [...new Set((contractsData || [])?.map(c => c?.client_id)?.filter(Boolean))];
      let clientMap = {};

      if (clientIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, email')
          ?.in('id', clientIds);

        clientMap = (profiles || [])?.reduce((acc, p) => {
          acc[p?.id] = p;
          return acc;
        }, {});
      }

      const enriched = (contractsData || [])?.map(c => ({
        ...c,
        client: clientMap[c?.client_id] || null,
      }));

      setContracts(enriched);
    } catch (error) {
      console.error('Load contracts error:', error);
      toast?.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    offered: 'bg-info/10 text-info',
    accepted: 'bg-success/10 text-success',
    in_progress: 'bg-primary/10 text-primary',
    completed: 'bg-success/10 text-success',
    cancelled: 'bg-error/10 text-error',
    disputed: 'bg-warning/10 text-warning'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Contract Management</h3>
        <div className="text-sm text-muted-foreground">
          Total Contracts: {contracts?.length}
        </div>
      </div>

      {contracts?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="FileText" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No contracts found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contracts?.map((contract) => (
            <div
              key={contract?.id}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-elevation-md transition-smooth"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-2">
                    <h4 className="font-semibold text-foreground">{contract?.title}</h4>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        statusColors?.[contract?.status] || 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {contract?.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Contract #:</span>
                      <span className="text-foreground ml-2">{contract?.contract_number}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="text-foreground ml-2 capitalize">{contract?.contract_type}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Expert:</span>
                      <span className="text-foreground ml-2">{contract?.expert?.full_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Client:</span>
                      <span className="text-foreground ml-2">{contract?.client?.email}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Commission:</span>
                      <span className="text-foreground ml-2">
                        {contract?.commission_pct
                          ? `${contract?.commission_pct}% (Contract Override)`
                          : contract?.expert?.commission_pct
                          ? `${contract?.expert?.commission_pct}% (Expert Default)`
                          : '10% (Platform Default)'}
                      </span>
                    </div>
                    {contract?.total_contract_value && (
                      <div>
                        <span className="text-muted-foreground">Value:</span>
                        <span className="text-foreground ml-2">
                          ${contract?.total_contract_value?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractManagement;